import React, { useCallback, useMemo, useRef, useState } from 'react'
import { DateRange as ExternalDateRange } from 'react-date-range'
import PropTypes from 'prop-types'
import classnames from 'classnames'

import SelectDropdown from '../../../components/SelectDropdown'
import Icon from '../../../components/Icon'

import useDateRange from './hooks'

import { ReactComponent as CalendarIcon } from '../../../assets/icons/calendar-icon2.svg'

import { getInitialDatePeriod } from '../../../helpers/date'

import useStyles from './styles'

const inputRanges = []

function DateRange({
  dateRangeData,
  onDateRangeChange,
  minDate,
  maxDate,
  isDisabled,
  isFloatingDropdown,
  // styles
  dropdownClassName,
  rangeClassName,
  dateRangesClassName,
  dayContentRenderer,
  dateRangeClassName
}) {
  const classes = useStyles()

  const [datePeriod, setDatePeriod] = useState(getInitialDatePeriod(dateRangeData))

  const { isOpen, setIsOpen, rangeColors, dateRangeLabel } = useDateRange(datePeriod)

  const shownDate = useMemo(() => (datePeriod && datePeriod.startDate) || minDate, [datePeriod, minDate])

  // ref for tracking start date selection status
  const isStartDateSelected = useRef(false)

  const handleDropdownVisibility = useCallback(
    show => {
      setIsOpen(show)
      if (isOpen && !show) {
        // return to default on close
        setDatePeriod(getInitialDatePeriod(dateRangeData))

        // reset isStartDateSelected state when user closes without selection
        isStartDateSelected.current = false
      }
    },
    [isOpen, setIsOpen, dateRangeData]
  )

  const handleDateRangeChange = useCallback(
    period => {
      setDatePeriod(period.selection)
      if (isStartDateSelected.current) {
        // we want to execute onDateRangeChange callback only when user selects full date range
        // for that reason we add extra state using ref to track double selection
        onDateRangeChange(period.selection)
      } else {
        isStartDateSelected.current = true
      }
    },
    [onDateRangeChange]
  )

  const handleFocusChange = useCallback(
    focus => {
      if (focus && !focus[1]) {
        // close dropdown when endDate is selected:
        setIsOpen(false)
        isStartDateSelected.current = false
      }
    },
    [setIsOpen]
  )

  // ExternalDateRange supports to have multiple ranges in one time, as we use only one - we set only first array element make sure it memoized to avoid new array create each time
  const ranges = useMemo(() => [datePeriod], [datePeriod])

  return (
    <SelectDropdown
      className={classnames('field', classes.dateRange, dateRangeClassName)}
      value={dateRangeLabel || ''}
      placeholder={'Start date - End date'}
      isManuallyOpened={isOpen}
      onVisibilityChange={handleDropdownVisibility}
      isDisabled={isDisabled}
      isFloatingDropdown={isFloatingDropdown}
      dropdownClassName={dropdownClassName}
      Icon={
        <Icon className={classes.calendarIcon}>
          <CalendarIcon />
        </Icon>
      }
    >
      <div className={classes.dateRangeDropdown}>
        <div className={classnames(classes.dateRanges, dateRangesClassName)}>
          <ExternalDateRange
            // start week from Monday
            weekStartsOn={1}
            onChange={handleDateRangeChange}
            className={classnames(classes.range, rangeClassName)}
            showDateDisplay={false}
            ranges={ranges}
            inputRanges={inputRanges}
            rangeColors={rangeColors}
            onRangeFocusChange={handleFocusChange}
            shownDate={shownDate && new Date(shownDate)}
            minDate={minDate && new Date(minDate)}
            maxDate={maxDate && new Date(maxDate)}
            direction="horizontal"
            dayContentRenderer={dayContentRenderer}
          />
        </div>
      </div>
    </SelectDropdown>
  )
}

DateRange.propTypes = {
  dateRangeData: PropTypes.shape({
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date)
  }),
  onDateRangeChange: PropTypes.func.isRequired,
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  isDisabled: PropTypes.bool,
  isFloatingDropdown: PropTypes.bool,
  dropdownClassName: PropTypes.string,
  rangeClassName: PropTypes.string,
  dateRangesClassName: PropTypes.string,
  dayContentRenderer: PropTypes.func
}

export default DateRange
